import { useMutation, useQuery } from '@apollo/client';
import { Link, Navigate, useParams } from 'react-router-dom';
import { Button, Container, Header, Icon, Label, Loader, Message, Segment, Table } from 'semantic-ui-react';

import { ResponsiveContainer } from '../../../components/ResponsiveContainer';
import { useContentReviewerGate } from '../../../hooks/isContentReviewer';
import { PATHS } from '../../../paths';
import { parseApiDate } from '../../../utils/date';
import { POLL_STATUS_LABEL, pollResultsCsv } from '../../../utils/poll';
import { CLOSE_POLL, GET_POLL_RESULTS } from './queries';

type Respondent = {
    membershipId: number;
    name: string | null;
    organisationName: string | null;
    answers: string[];
    otherText: string | null;
    respondedAt: string | null;
};

const when = (iso: string | null | undefined) => parseApiDate(iso)?.toLocaleString() ?? '—';
const who = (r: Respondent) => (r.organisationName ? `${r.name} (${r.organisationName})` : r.name ?? 'Unknown member');

/** One poll's results, with names. Content reviewers of the poll's organisation only. */
export function PollResultsView() {
    const { postId } = useParams();
    const id = Number(postId);
    const { loading: meLoading, isContentReviewer } = useContentReviewerGate();
    const { data, loading, error, refetch } = useQuery(GET_POLL_RESULTS, {
        variables: { postId: id },
        skip: !isContentReviewer || !Number.isInteger(id),
        fetchPolicy: 'cache-and-network',
    });
    const [closePoll, { loading: closing, error: closeError }] = useMutation(CLOSE_POLL, { onCompleted: () => refetch() });

    if (!meLoading && !isContentReviewer) return <Navigate to={PATHS.CLASSES} replace />;

    const results = data?.pollResults;
    if (!results) {
        return (
            <ResponsiveContainer>
                <Container style={{ padding: '2em 0' }}>
                    {loading || meLoading ? <Loader active inline="centered" /> : <Message error content={error?.message ?? 'That poll could not be found.'} />}
                </Container>
            </ResponsiveContainer>
        );
    }

    const { poll, options, otherAnswers, respondents } = results;
    const status = POLL_STATUS_LABEL[poll.status] ?? { text: poll.status, color: 'grey' as const };
    const total = poll.respondentCount as number;
    const canToggle = poll.status === 'LIVE' || poll.status === 'CLOSED';

    const downloadCsv = () => {
        const blob = new Blob([pollResultsCsv(poll.question ?? '', respondents)], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `poll-${poll.postId}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <ResponsiveContainer>
            <Container style={{ padding: '2em 0' }}>
                <Link to={PATHS.POLLS}><Icon name="arrow left" />All polls</Link>

                <Header as="h1" style={{ marginTop: '0.5em' }}>
                    {poll.question || 'Untitled poll'}
                    <Header.Subheader>
                        <Label size="small" color={status.color} basic={status.color === 'grey'}>{status.text}</Label>
                        {' '}{poll.allowMultiple ? 'Several answers allowed' : 'One answer each'}
                        {poll.allowOther ? ' · “Something else” on' : ''}
                        {poll.closesAt ? ` · closes ${when(poll.closesAt)}` : ''}
                    </Header.Subheader>
                </Header>
                {poll.description && <p>{poll.description}</p>}

                <div style={{ display: 'flex', gap: '0.5em', flexWrap: 'wrap', marginBottom: '1em' }}>
                    {canToggle && (
                        <Button
                            size="small"
                            loading={closing}
                            icon={poll.isClosed ? 'play' : 'stop'}
                            content={poll.isClosed ? 'Reopen poll' : 'Close poll'}
                            onClick={() => closePoll({ variables: { postId: poll.postId, closed: !poll.isClosed } })}
                        />
                    )}
                    <Button size="small" basic icon="download" content="Download CSV" disabled={total === 0} onClick={downloadCsv} />
                    {(poll.status === 'LIVE' || poll.status === 'CLOSED' || poll.status === 'TAKEN_DOWN') && (
                        <Button
                            size="small"
                            basic
                            as={Link}
                            to={PATHS.POST_SENT.replace(':postId', String(poll.postId))}
                            icon="external"
                            content="Open the post"
                        />
                    )}
                </div>
                {closeError && <Message error content={closeError.message} />}

                <Segment>
                    <Header as="h3">
                        {total} {total === 1 ? 'person has' : 'people have'} answered
                    </Header>
                    {options.map((option: any) => {
                        const pct = total === 0 ? 0 : Math.round((option.count / total) * 100);
                        return (
                            <div key={option.id} style={{ marginBottom: '1em' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <strong>{option.label}</strong>
                                    <span>{option.count} · {pct}%</span>
                                </div>
                                <div style={{ background: '#eee', borderRadius: 4, height: 10, margin: '0.3em 0' }}>
                                    <div style={{ width: `${pct}%`, background: '#21ba45', height: '100%', borderRadius: 4 }} />
                                </div>
                                {option.respondents.length > 0 && (
                                    <div style={{ fontSize: '0.85em', color: 'rgba(0,0,0,.6)' }}>
                                        {option.respondents.map(who).join(', ')}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                    {poll.allowMultiple && total > 0 && (
                        <div style={{ fontSize: '0.85em', color: 'rgba(0,0,0,.6)' }}>
                            People could choose several answers, so the percentages add up to more than 100.
                        </div>
                    )}
                </Segment>

                {poll.allowOther && (
                    <Segment>
                        <Header as="h3">“Something else” ({otherAnswers.length})</Header>
                        {otherAnswers.length === 0 && <p style={{ color: 'rgba(0,0,0,.6)' }}>Nobody has written their own answer yet.</p>}
                        {otherAnswers.map((r: Respondent) => (
                            <Message key={r.membershipId} size="small">
                                <Message.Header>{who(r)}</Message.Header>
                                <p style={{ whiteSpace: 'pre-wrap' }}>{r.otherText}</p>
                            </Message>
                        ))}
                    </Segment>
                )}

                <Segment>
                    <Header as="h3">Everyone who answered</Header>
                    {respondents.length === 0 ? (
                        <p style={{ color: 'rgba(0,0,0,.6)' }}>No answers yet.</p>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <Table compact unstackable>
                                <Table.Header>
                                    <Table.Row>
                                        <Table.HeaderCell>Name</Table.HeaderCell>
                                        <Table.HeaderCell>Organisation</Table.HeaderCell>
                                        <Table.HeaderCell>Answer</Table.HeaderCell>
                                        <Table.HeaderCell>When</Table.HeaderCell>
                                    </Table.Row>
                                </Table.Header>
                                <Table.Body>
                                    {respondents.map((r: Respondent) => (
                                        <Table.Row key={r.membershipId}>
                                            <Table.Cell>{r.name}</Table.Cell>
                                            <Table.Cell>{r.organisationName ?? '—'}</Table.Cell>
                                            <Table.Cell>
                                                {[...r.answers, ...(r.otherText ? [`“${r.otherText}”`] : [])].join(', ')}
                                            </Table.Cell>
                                            <Table.Cell>{when(r.respondedAt)}</Table.Cell>
                                        </Table.Row>
                                    ))}
                                </Table.Body>
                            </Table>
                        </div>
                    )}
                </Segment>
            </Container>
        </ResponsiveContainer>
    );
}
